import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import type { MatchedSource } from "../../../../types/incident";

type SourceRow = {
  id: string;
  name: string;
  type: string;
  value: string;
};

const sourceGridSx = {
  border: 0,
  "& .MuiDataGrid-cell": { alignItems: "center", py: 0.5 },
  "& .MuiDataGrid-columnHeaders": {
    bgcolor: "action.hover",
    fontWeight: 700,
    fontSize: "0.75rem",
  },
} as const;

export default function IncidentSourcesSection({
  matchedSources,
}: {
  matchedSources?: MatchedSource[] | null;
}) {
  const { t } = useTranslation();

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: "name",
        headerName: t("common.name"),
        flex: 1,
        minWidth: 120,
        sortable: false,
      },
      {
        field: "type",
        headerName: t("common.type"),
        width: 130,
        sortable: false,
        valueFormatter: (value: string) =>
          value ? t(`options.contactType.${value}`, { defaultValue: value }) : "—",
      },
      {
        field: "value",
        headerName: t("common.value"),
        flex: 1.2,
        minWidth: 140,
        sortable: false,
      },
    ],
    [t],
  );

  const tableRows = useMemo<SourceRow[]>(
    () =>
      (Array.isArray(matchedSources) ? matchedSources : []).map((item, index) => ({
        id: `matched-${index}`,
        name: item.name?.trim() || "—",
        type: item.type?.trim() || "—",
        value: item.value?.trim() || "—",
      })),
    [matchedSources],
  );

  if (!Array.isArray(matchedSources) || matchedSources.length === 0) {
    return null;
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 1,
        mb: 1.5,
        overflow: "hidden",
        bgcolor: "background.paper",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          px: 1.25,
          py: 0.65,
          borderBottom: 1,
          borderColor: "divider",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        }}
      >
        <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
          {t("incidents.sources")}
        </Typography>
      </Box>
      <DataGrid
        autoHeight
        rows={tableRows}
        columns={columns}
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
        showCellVerticalBorder
        showColumnVerticalBorder
        columnHeaderHeight={36}
        rowHeight={40}
        localeText={{ noRowsLabel: t("incidents.noSourcesConfigured") }}
        sx={sourceGridSx}
      />
    </Paper>
  );
}
